import { type ReactNode, useMemo } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { canAccessAdminRoute, getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'

interface MarketingShellProps {
  title: string
  description?: string
  /** Optional right-aligned page action (e.g. a date-range picker) rendered
   *  next to the heading on the same row, mirroring the Reports shell so the
   *  two pages share a consistent layout. */
  actions?: ReactNode
  children: ReactNode
}

interface MarketingTab {
  id: string
  label: string
  href: string
}

const TABS: MarketingTab[] = [
  { id: 'overview', label: 'Overview', href: '/admin/marketing/overview' },
  { id: 'promo-codes', label: 'Promo codes', href: '/admin/marketing/promo-codes' },
  { id: 'loyalty', label: 'Loyalty program', href: '/admin/marketing/loyalty' },
  { id: 'enrolment', label: 'Enrolment', href: '/admin/marketing/enrolment' },
  { id: 'members', label: 'Members', href: '/admin/marketing/members' },
  { id: 'qr-codes', label: 'QR codes', href: '/admin/marketing/qr-codes' },
  { id: 'segments', label: 'Segments', href: '/admin/marketing/segments' },
  { id: 'campaigns', label: 'Campaigns', href: '/admin/marketing/campaigns' },
  { id: 'card-promos', label: 'Bank cards', href: '/admin/marketing/card-promos' },
  // Temporarily hidden — route still exists at /admin/marketing/posters.
  // { id: 'posters', label: 'Posters', href: '/admin/marketing/posters' },
]

export function MarketingShell({ title, description, actions, children }: MarketingShellProps) {
  const location = useLocation()

  // Marketing is admin-only except for Bank cards, which managers own too
  // (staff-roles.ts). Filtering here rather than rendering all eight tabs
  // keeps a manager from clicking straight into a route guard bounce.
  const tabs = useMemo(() => {
    const role = getStoredUserRole()
    if (role === 'admin' || role === '') return TABS
    return TABS.filter((t) => canAccessAdminRoute(role, t.href))
  }, [])

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {description && (
            <p className="mt-1 text-muted-foreground max-w-3xl">{description}</p>
          )}
        </div>
        {actions && <div className="lg:pt-1">{actions}</div>}
      </div>

      {/* A one-tab row is noise, not navigation (the manager case). */}
      {tabs.length > 1 && (
      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="navigation" aria-label="Marketing sections">
        {tabs.map((tab) => {
          const isActive =
            location.pathname === tab.href ||
            location.pathname.startsWith(tab.href + '/')
          return (
            <Link
              key={tab.id}
              to={tab.href}
              className={cn(
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </div>
      )}

      {children}
    </div>
  )
}
